import { defineStore } from "pinia";

import { computed, ref, watchEffect } from "vue";
import Config from "@/common/config";
import type {
  AddressInformation,
  QuotationData,
  Transport,
} from "@/common/types/chargeWeight";
import { useChargeweightPersistedStore } from "@/stores/chargeweightPersisted";
import { transportsAPI } from "@/api/transports";
import { useNotifications } from "@/common/notification";
import i18n from "@/localization/i18n";
import { cloneDeep } from "lodash-es";
import { useAuthStore } from "@/stores/auth";
import { quotationAPI } from "@/api/quotation";
import { useI18n } from "vue-i18n";

export const useQuotationStore = defineStore(
  "quotation",
  () => {
    const chargeWeightStore = useChargeweightPersistedStore();
    const LB_TO_KG = 0.45359237;
    const FT3_TO_M3 = 0.0283168;
    const { locale } = useI18n();
    const isGerman = computed(() => locale.value === "de");
    const quotationData = ref<QuotationData>(cloneDeep(DEFAULT_QUOTATION_DATA));
    const activeCard = ref<number>(1);
    const transportationRequestData = computed(() => {
      const { chargeWeight, additionalInformation, ...otherDetails } =
        quotationData.value;
      return otherDetails;
    });
    const routings = ref<Transport[]>([]);
    const transports = ref<Transport[]>([]);
    const selectedTransport = ref<Transport | undefined>();
    const transportsLoading = ref(false);
    const mailSent = ref(false);
    const transportsFound = ref(false);

    const fetchRoutings = async (): Promise<void> => {
      transportsLoading.value = true;

      try {
        const { data, status } = await transportsAPI.getRoutings(
          transportationRequestData.value,
        );

        if (status === 200) {
          routings.value = data as Transport[];
        }
      } catch (error) {
        pushInternalErrorNotification();
      } finally {
        transportsLoading.value = false;
      }
    };

    const fetchTransports = async () => {
      try {
        const { metricMeasurement, metricVolumeMeasurement } =
          useChargeweightPersistedStore();
        const payload = cloneDeep(quotationData.value);

        if (!metricMeasurement) {
          payload.chargeWeight.totalWeight *= LB_TO_KG;
        }

        if (!metricVolumeMeasurement) {
          payload.chargeWeight.totalVolume *= FT3_TO_M3;
        }

        const response = await transportsAPI.getTransports(payload);
        if (response.status === 200) {
          transports.value = response.data;
          transportsFound.value = true;
        }
      } catch (error) {
        pushInternalErrorNotification();
        console.error("Failed to fetch price and service type:", error);
      }
    };

    const sendMails = async (userId: string | undefined) => {
      try {
        const { status } = await quotationAPI.sendMails(
          quotationData.value,
          transportsFound.value,
          userId,
          selectedTransport.value,
        );
        if (status !== 200) {
          pushInternalErrorNotification();
        }
        mailSent.value = true;
      } catch (error) {
        pushInternalErrorNotification();
        console.log("Failed sending mails: ", error);
      }
    };

    const pushInternalErrorNotification = () => {
      useNotifications().push("error", i18n.global.t("internalError"));
    };

    function getLocalizedTransportData(transport: Transport) {
      return {
        polName: isGerman.value ? transport.pol.nameDE : transport.pol.nameEN,
        polCountry: isGerman.value
          ? transport.pol.countryDE
          : transport.pol.countryEN,
        podName: isGerman.value ? transport.pod.nameDE : transport.pod.nameEN,
        podCountry: isGerman.value
          ? transport.pod.countryDE
          : transport.pod.countryEN,
      };
    }

    const formatAddress = (addressData: AddressInformation) => {
      const { company, address, city, zip, country } = addressData;
      return `${company}, ${address}, ${city}, ${zip}, ${country.toUpperCase()}`;
    };

    const pickupAddressDisplayString = computed(() =>
      formatAddress(quotationData.value.pickup),
    );
    const deliveryAddressDisplayString = computed(() =>
      formatAddress(quotationData.value.delivery),
    );

    function resetQuotationData() {
      Object.assign(quotationData.value, cloneDeep(DEFAULT_QUOTATION_DATA));
      quotationData.value.customer = cloneDeep(
        useAuthStore().currentUser?.visibility.nodeID ?? "",
      );
      transports.value = [];
      routings.value = [];
      mailSent.value = false;
      selectedTransport.value = undefined;
      transportsFound.value = false;
      activeCard.value = 1;
    }

    watchEffect(() => {
      quotationData.value.chargeWeight.totalWeight =
        chargeWeightStore.weightInput;
      quotationData.value.chargeWeight.totalVolume =
        chargeWeightStore.volumeInput;
      quotationData.value.chargeWeight.cwAir =
        chargeWeightStore.shippingMethods.find((x) => x.name === "AIR")
          ?.weight ?? 0;
      quotationData.value.chargeWeight.cwCourier =
        chargeWeightStore.shippingMethods.find((x) => x.name === "COURIER")
          ?.weight ?? 0;
      quotationData.value.chargeWeight.cwOcean =
        chargeWeightStore.shippingMethods.find((x) => x.name === "OCEAN")
          ?.weight ?? 0;
      quotationData.value.chargeWeight.cwTruck =
        chargeWeightStore.shippingMethods.find((x) => x.name === "TRUCK")
          ?.weight ?? 0;
      quotationData.value.chargeWeight.freightDetailRows =
        chargeWeightStore.completedFreightDetailRows;
    });

    return {
      quotationData,
      activeCard,
      resetQuotationData,
      transports,
      routings,
      pickupAddressDisplayString,
      deliveryAddressDisplayString,
      transportationRequestData,
      fetchTransports,
      fetchRoutings,
      transportsLoading,
      selectedTransport,
      sendMails,
      getLocalizedTransportData,
      mailSent,
      transportsFound,
    };
  },
  {
    persist: {
      key: Config.quotationDataPersistKey,
    },
  },
);

export const DEFAULT_QUOTATION_DATA: QuotationData = {
  customer: "",
  delivery: {
    addressNr: "",
    company: "",
    address: "",
    city: "",
    zip: "",
    country: "",
  },
  pickup: {
    addressNr: "",
    company: "",
    address: "",
    city: "",
    zip: "",
    country: "",
  },
  chargeWeight: {
    totalWeight: 0,
    totalVolume: 0,
    cwAir: 0,
    cwCourier: 0,
    cwOcean: 0,
    cwTruck: 0,
    freightDetailRows: [],
  },
  availableFrom: null,
  dueDate: null,
  additionalInformation: {
    incoterm: "",
    hasDangerousGoods: false,
    hasInsurance: false,
    remarks: "",
    valueOfGoods: 0,
    currency: "USD",
  },
};
